import React, {useCallback, useEffect, useState} from 'react';
import {Col, Container, Row} from 'reactstrap';
import {Location} from '@reach/router';
import queryString from 'query-string';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCheck} from '@fortawesome/pro-light-svg-icons/faCheck';
import {faTimes} from '@fortawesome/pro-light-svg-icons/faTimes';
import {faArrowDown} from '@fortawesome/pro-regular-svg-icons';
import classNames from 'classnames';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState} from 'funnel-schedule/phase-context';
import {useUserState} from 'contexts/user';
import useBalanceText from 'utils/useBalanceText';

import PageMeta from 'components/PageMeta';
import Section, {SectionTriangle, SectionTriangleSpeakerCollapse} from 'components/Elements/Section';
import Header from 'components/Elements/Header';
import Video from 'components/Video';
import {ListIcon} from 'components/Elements/IconList';
import TimerSection from 'components/Landing/TimerSection';
import MasterclassInfographicForm from 'components/MasterclassInfographicForm';
import QuizMasterclassOptin from 'components/Forms/QuizMasterclassOptin';
import OptinModal, {OptinModalTrigger} from 'components/OptinModal';
import ModalExitLanding from 'components/Modals/ModalExitLanding';
import ShareButtons from 'components/Social/ShareButtons';
import Footer from 'components/Elements/Footer';

import {questions} from 'data/questions.json';
import affids from 'data/affids.json';

export default function Index(props) {
  return (
    <Location>
      {({location, navigate}) => <MasterClassIndex location={location} navigate={navigate} {...props} />}
    </Location>
  );
}
function MasterClassIndex({
  location = {},
  noShare = false,
  noExitIntent = false,
  linkLogo = false,
  slug = 'masterclass',
  navigate,
  variant = 'default',
  ...props
}) {
  const {email} = useUserState();
  const qs = typeof window !== 'undefined' ? queryString.parse(window.location.search) : {};
  const {fromInfographic} = qs.fromInfographic ? qs : location.state || {};
  const {fromQuiz, answerSet, score, answers} = qs.fromQuiz ? qs : location.state || {};
  const navigateWatch = useCallback(() => navigate('/masterclass/watch/'), []);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const pathName = window.location.pathname;
      const pathRegex = new RegExp(`\/${slug}\/`, 'g');
      if (!pathName.match(pathRegex)) {
        navigate(`/${slug}/` + location.search);
      }
    }
  }, []);

  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before',
        launchStart: '',
        launchEnd: '',
        launchTitle: ''
      },
      special: specialSchedule = {}
    }
  } = usePhaseState();

  const launchEnd = (
    specialSchedule.launchEnd ? new Date(specialSchedule.launchEnd) : new Date(defaultSchedule.launchEnd)
  ).getTime();

  let partnerName;
  if (typeof window !== 'undefined') {
    const qsAffiliate = queryString.parse(window.location.search);
    if (qsAffiliate.affid && affids[qsAffiliate.affid]) {
      ({partnerName} = affids[qsAffiliate.affid]);
    }
  }

  const phase = partnerName && specialSchedule.phase ? specialSchedule.phase : defaultSchedule.phase;

  function Logo({className = null, src = 'https://cdn.foodrevolution.org/hbc/hbc-bbm-icon-logo.svg'}) {
    return (
      <img src={src} alt="break breakthroughs masterclass logo" style={{maxHeight: '90px'}} className={className} />
    );
  }

  const ctaButton =
    (fromInfographic && email) || (fromQuiz && email) ? (
      <>
        {fromInfographic && <MasterclassInfographicForm fromInfographic={fromInfographic} />}
        {fromQuiz && <QuizMasterclassOptin fromQuiz={fromQuiz} />}
      </>
    ) : (
      <OptinModalTrigger>
        Reserve Your <br className="d-sm-none d-lg-block d-xl-none" />
        <i>Free</i> Spot Now
      </OptinModalTrigger>
    );

  function Question({question, name, options, value, answer, theirAnswer = null, ...props}) {
    return (
      <div id={value} className="quiz-question-wrap">
        <h5 className="section-heading">
          <b>{question}</b>
        </h5>
        {theirAnswer ? (
          <div className="quiz-answer-incorrect text-red mb-2">
            <span className="quiz-answer-icon">
              <FontAwesomeIcon icon={faTimes} />
            </span>{' '}
            Your Answer: {options[theirAnswer]}
          </div>
        ) : null}
        <div className="quiz-answer-correct text-green">
          <span className="quiz-answer-icon">
            <FontAwesomeIcon icon={faCheck} />
          </span>{' '}
          Correct Answer: {options[answer]}
        </div>
      </div>
    );
  }
  function QuestionMap() {
    return (
      <div className="quiz-answers-wrap text-left">
        {questions.map((question, i) =>
          !answerSet[i] ? <Question {...question} key={i} theirAnswer={answers[i]} /> : null
        )}
      </div>
    );
  }

  useBalanceText();
  return (
    <div className="page d-flex flex-column">
      <PageMeta
        title="Join the FREE Brain Breakthroughs Masterclass."
        description="Discover the key to thriving brain health at any age with FRN CEO, Ocean Robbins, and leading neurologists Dean and Ayesha Sherzai, MD in this free Masterclass."
      />
      <div className="page-content">
        {partnerName && (
          <>
            <Section id="partner-banner" color="dark-gray" innerClass="text-center text-white py-4" angle="none">
              <Container>
                <Row>
                  <Col>
                    <p className="mb-0">
                      {partnerName} invites you to join the <i>Brain Breakthroughs Masterclass</i>
                    </p>
                    {specialSchedule.isLastChance && specialSchedule.lastChanceText ? (
                      <p className="mt-3 mb-0">{specialSchedule.lastChanceText}</p>
                    ) : null}
                  </Col>
                </Row>
              </Container>
            </Section>
          </>
        )}
        {fromInfographic && (
          <section className="section-infographic-info background-light-gray py-4">
            <Container>
              <Row className="d-flex align-items-center">
                <Col xs="3" md="2" className="text-center">
                  <img
                    className="img-infographic img-fluid"
                    src="https://cdn.foodrevolution.org/hbc/hbc-infographic-3d.png"
                  />
                </Col>
                <Col xs="9" md="10">
                  Thanks for taking an interest in your brain health! Your poster of the{' '}
                  <i className="text-800">Top 10 Foods To Eat & Avoid For Brain Health</i> should be in your email inbox
                  in a few minutes.
                </Col>
              </Row>
            </Container>
          </section>
        )}

        <Header className="background-white header-wrap" style="light" />

        {fromQuiz && (
          <>
            {score < 100 ? (
              <Section
                id="section-quiz-info"
                className="section-angle-first"
                innerClass="py-5"
                color="light-gray"
                angle="none"
              >
                <Container>
                  <Row className="d-flex align-items-center">
                    <Col className="text-center">
                      <h4 className="section-heading text-400 mb-3">Thank you for taking this brain health quiz.</h4>
                      <h2 className="mt-0 mb-3">You scored: {score}%</h2>
                      <h3 className="mt-0">
                        See below for what you missed — plus the right answers! (You got all the rest right!)
                      </h3>

                      <Row className="d-flex justify-content-center">
                        <Col lg="10">
                          <QuestionMap />
                        </Col>
                      </Row>

                      {score <= 69 ? (
                        <>
                          <h3 className="mb-0">
                            Want to learn more about how to keep your brain healthy? We have exciting news for you!
                          </h3>
                        </>
                      ) : null}

                      {score >= 70 && score <= 99 ? (
                        <>
                          <h3 className="mb-2">Not too bad! You know quite a bit about brain health.</h3>
                          <h3 className="m-0">Now take it to the next level.</h3>
                        </>
                      ) : null}

                      <Row className="d-flex justify-content-center mt-5">
                        <Col sm="10" md="8" lg="6" xl="5">
                          <div className="arrows d-flex justify-content-between">
                            <FontAwesomeIcon className="text-icon" icon={faArrowDown} />
                            <FontAwesomeIcon className="text-icon" icon={faArrowDown} />
                            <FontAwesomeIcon className="text-icon" icon={faArrowDown} />
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Container>
              </Section>
            ) : (
              <Section id="section-quiz-info" color="light-gray" innerClass="text-center py-4" angle="none">
                <Container>
                  <Row>
                    <Col>
                      <h4 className="section-heading text-400 mb-3">Thank you for taking this brain health quiz.</h4>
                      <h2 className="m-0">You scored: {score}%</h2>
                    </Col>
                  </Row>
                </Container>
              </Section>
            )}
          </>
        )}

        <SectionTriangle id="landing-poster-header" imgUrl="" color="black" />

        <SectionTriangle id="landing-poster-video" color="white" type="both" className="text-center">
          <Container>
            <Row>
              <Col>
                <div className="optin-box background-white rounded p-4 p-lg-5 box-shadow-black-50 text-center">
                  <h5 className="section-heading">
                    <i>Now Playing FREE For A Limited Time!</i>
                  </h5>
                  <Logo className="mb-4" />
                  <h1 className="text-h3 mt-0 mb-3">
                    {variant === 'ad' ? (
                      <>Discover 10 Potent Brain Health Breakthroughs</>
                    ) : (
                      <>Rewire Your Brain For Health, End Brain Fog, and Slash Your Risk of Dementia</>
                    )}
                  </h1>
                  {variant === 'ad' ? (
                    <h5 className="text-400 mt-0 mb-5">
                      Join Dean and Ayesha Sherzai, MD, two of the world’s foremost neurologists, for a Masterclass that
                      shares the latest neuroscience on Alzheimer’s prevention and peak brain performance.
                    </h5>
                  ) : (
                    <>
                      <h5 className="text-400 mt-0 mb-5">
                        Join Dean and Ayesha Sherzai, MD, two of the world’s foremost neurologists, as they reveal what
                        the world’s sharpest minds have in common.
                      </h5>
                    </>
                  )}
                  {ctaButton}
                </div>
              </Col>
            </Row>
            <Row className="mt-4 mt-lg-5">
              <Col>
                <h5 className="text-400 m-0">
                  Get the <i>latest neuroscience</i> on Alzheimer’s prevention and peak brain performance with the{' '}
                  <b>Brain Breakthroughs Masterclass</b>
                </h5>
              </Col>
            </Row>
            <Row className="justify-content-center mt-5">
              <Col lg="10" className="video-col px-0 px-sm-3">
                <Video
                  url="https://www.youtube.com/watch?v=Z0FOPUU3k88"
                  label="HBC - Masterclass Trailer"
                  thumbnail="https://cdn.foodrevolution.org/hbc/videothumb/hbc-landingpage-dynamicthumbnail-20230711-v1.gif"
                  noButton
                />
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle id="" color="purple" type="bottom" className="text-center text-white">
          <Container>
            <Row>
              <Col>
                <h3 className="section-only-heading text-400">
                  In This <b>90-Minute Masterclass,</b> {variant === 'ad' ? 'We’ll Reveal' : 'You’ll Discover'}:
                </h3>
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle id="" color="white" type="bottom">
          <Container>
            <Row>
              <Col>
                {fromQuiz ? (
                  <ListIcon iconColor="purple" className="m-0">
                    <>
                      <p>
                        Why you should aim for a <b>brain-healthy</b> diet and lifestyle <b>today</b>
                      </p>
                    </>
                    <>
                      <p>
                        How you can build new neural connections, <b>no matter your age</b>
                      </p>
                    </>
                    <>
                      <p>
                        The difference between the different <b>types of stress,</b> and how they{' '}
                        <b>affect your brain</b>
                      </p>
                    </>
                    <>
                      <p>
                        Why brisk walks can <b>reduce Alzheimer’s risk by 40%,</b> and the importance of leg strength
                        for brain health
                      </p>
                    </>
                    <>
                      <p>
                        The <b>superfoods your brain loves</b>
                      </p>
                    </>
                    <>
                      <p>
                        How you can create <b>long-term protection</b> for your brain from diseases like{' '}
                        <b>Alzheimer’s and Dementia</b>
                      </p>
                    </>
                    <>
                      <p>
                        Why your brain needs both <b>complexity</b> AND <b>consistency</b>
                      </p>
                    </>
                    <>
                      <p>
                        The importance of winding down for <b>supportive and nourishing sleep</b>
                      </p>
                    </>
                    <>
                      <p>
                        Why SMART goals work to establish positive <b>healthy habits that stick</b>
                      </p>
                    </>
                    <>
                      <p>
                        <b>
                          <i>And so much more...</i>
                        </b>
                      </p>
                    </>
                  </ListIcon>
                ) : (
                  <ListIcon iconColor="purple" className="m-0">
                    <>
                      {variant === 'ad' ? (
                        <>
                          <h5 className="section-heading text-purple mb-1">
                            Breaking discoveries in neuroscience that may help us <i>switch off genes</i> that
                            contribute to dementia
                          </h5>
                          <p>
                            Many people worry there’s nothing we can do about mental decline as we age. The truth is
                            there’s a lot we can do to <b>slash risks and even renew our brain’s health!</b>
                          </p>
                        </>
                      ) : (
                        <>
                          <h5 className="section-heading text-purple mb-1">
                            Breaking discoveries in neuroscience that can help you switch off genes that contribute to
                            dementia
                          </h5>
                          <p>
                            We used to think that dementia was all genetic, and if you had the wrong genes, there was
                            nothing you could do about it. Now we know that’s almost completely false; there’s a lot you
                            can do to slash your risk for Alzheimer’s and other dementias. But research insights usually
                            take 15 years to influence clinical practice, so you may not hear this information from your
                            doctor.
                          </p>
                        </>
                      )}
                    </>
                    <>
                      {variant === 'ad' ? (
                        <>
                          <h5 className="section-heading text-purple mb-1">
                            Which foods to <i>stop eating right now</i> for excellent sleep, sharper thoughts, more
                            energy, and better memory
                          </h5>
                          <p>
                            What’s on the plate of people who live to be 100 years old and stay sharp as a tack? What do
                            they do to enjoy the <b>best life expectancy and quality of life in the world?</b> Forget
                            fads. Find out what the hard science says.
                          </p>
                        </>
                      ) : (
                        <>
                          <h5 className="section-heading text-purple mb-1">
                            Which foods to stop eating right now for excellent sleep, sharper thoughts, more energy, and
                            better memory
                          </h5>
                          <p>
                            What’s on the plate of people who live to be 100 years old and stay sharp as a tack? What do
                            they do to enjoy the best life expectancy and quality of life in the world? Forget fads and
                            anecdotes about “my aunt who smoked 5 packs a day and lived to be 97.” Find out what the
                            hard science says.
                          </p>
                        </>
                      )}
                    </>
                    <>
                      {variant === 'ad' ? (
                        <>
                          <h5 className="section-heading text-purple mb-1">
                            The easy way to rewire the brain and <i>create new healthy habits</i> that stick
                          </h5>
                          <p>
                            HINT: it has nothing to do with willpower! There are ways to use the brain’s
                            neurotransmitters to <b>permanently break a bad habit.</b>
                          </p>
                        </>
                      ) : (
                        <>
                          <h5 className="section-heading text-purple mb-1">
                            The easy way to rewire your brain and create new healthy habits that stick
                          </h5>
                          <p>
                            Tried to change and failed a hundred times? It has nothing to do with your willpower! Learn
                            how to use your own brain’s neurotransmitters to permanently break bad habits — even if
                            you’ve been trying and failing your whole life. You'll finally open your brain's user's
                            manual, and stop fighting against it.
                          </p>
                        </>
                      )}
                    </>
                    <>
                      {variant === 'ad' ? (
                        <>
                          <h5 className="section-heading text-purple mb-1">
                            The <i>dangerous brain health myths</i> that can shorten lifespan
                          </h5>
                          <p>
                            Until recently, the mysteries of the brain have led to wild (and even deadly) assumptions
                            about how the brain works. <b>Get the truth.</b>
                          </p>
                        </>
                      ) : (
                        <>
                          <h5 className="section-heading text-purple mb-1">
                            The dangerous brain health myths that shorten lives and devastate families
                          </h5>
                          <p>
                            Until recently, the mysteries of the brain have led to wild (and even deadly) assumptions
                            about its care and feeding — butter in your coffee, anyone? Get the truth. You could save
                            yourself or someone you love from tremendous misery.
                          </p>
                        </>
                      )}
                    </>
                    <>
                      {variant === 'ad' ? (
                        <>
                          <h5 className="section-heading text-purple mb-1">
                            Why The Brain Docs <i>walked away from a lucrative career</i> in pharmaceutical research
                          </h5>
                          <p>
                            Dean and Ayesha were told it would be the “death of their career.” Hear how their
                            controversial decision led them to{' '}
                            <b>
                              discover a <i>real</i> solution to the growing wave of brain-related disorders.
                            </b>
                          </p>
                        </>
                      ) : (
                        <>
                          <h5 className="section-heading text-purple mb-1">
                            The Nail-biting Story of How The Brain Docs walked away from a lucrative career in
                            pharmaceutical research
                          </h5>
                          <p>
                            Dean and Ayesha were told it would be the “death of their career.” Hear how their
                            controversial decision led them to the precipice — where they discovered a real solution to
                            the Alzheimer’s tsunami.
                          </p>
                        </>
                      )}
                    </>
                    <>
                      {variant === 'ad' ? (
                        <>
                          <h5 className="section-heading text-purple mb-1">
                            Receive <i>a special invitation</i> to have Dean and Ayesha walk you through implementing
                            everything you learn (for a massive discount)
                          </h5>
                          <p>
                            Want to do everything possible to avoid unnecessary heartache? Stay tuned to learn how Dean
                            and Ayesha are guiding people on the journey to better brain health (with a special offer
                            only available from the Masterclass).
                          </p>
                        </>
                      ) : (
                        <>
                          <h5 className="section-heading text-purple mb-1">
                            Receive a special invitation to have Dean and Ayesha walk you through implementing
                            everything you learn (for a massive discount)
                          </h5>
                          <p>
                            Want to do everything you can now, so you can protect yourself and your family from
                            unnecessary heartache? Stay tuned to learn how Dean and Ayesha can hold your hand and guide
                            you on the journey to better brain health (with a special offer only available from the
                            Masterclass).
                          </p>
                        </>
                      )}
                    </>
                  </ListIcon>
                )}
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle id="" color="purple" type="bottom" className="text-center text-white">
          <Container>
            <Row>
              <Col>
                <h3 className="section-only-heading">Meet Your Instructors</h3>
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangleSpeakerCollapse
          id=""
          imgUrl="https://cdn.foodrevolution.org/global/speakers/frn-guest_expert-portrait-square-deanayeshasherzai-opt.jpg"
          imgPosition="right"
          color="white"
          type="bottom"
          headlines={
            <>
              <h2 className="section-heading mb-2">Dean Sherzai, MD, PhD & Ayesha Sherzai, MD</h2>
              <h4 className="m-0 text-400">
                Cutting-edge neurologists, brain science researchers, and best-selling authors.
              </h4>
            </>
          }
          collapseHeader={
            <>
              <h5 className="text-400 m-0">Click Here to</h5>
              <h4 className="m-0">Learn More About The Brain Docs</h4>
            </>
          }
          collapseHeaderBgColor="black"
          content={
            <>
              <p>
                Dean Sherzai, MD, and Ayesha Sherzai, MD, are the authors of the books <i>The Alzheimer’s Solution</i>{' '}
                and{' '}
                <i>
                  The 30-Day Alzheimer's Solution: The Definitive Food and Lifestyle Guide to Preventing Cognitive
                  Decline.
                </i>{' '}
              </p>
              <p>
                They are co-directors of the Alzheimer’s Prevention Program at Loma Linda University, directors of the
                Healthy Minds Initiative, and creators of The NEURO Plan, a customizable brain health program that
                utilizes technology and neurological research to empower users to improve their brain health.
              </p>
              <p>
                They host the popular <i>Brain Health Revolution Podcast</i> and have been featured in{' '}
                <i>The Sunday Times,</i> <i>O Magazine,</i> <i>The Wall Street Journal,</i> and{' '}
                <i>The Huffington Post</i> and on <i>ABC News,</i> <i>CNN,</i> and <i>BBC Good Food.</i>
              </p>
            </>
          }
        />

        <SectionTriangle id="landing-host-v" color="purple" type="bottom" className="text-center text-white">
          <Container>
            <Row>
              <Col>
                <h3 className="section-only-heading">Meet Your Host</h3>
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangleSpeakerCollapse
          id=""
          imgUrl="https://cdn.foodrevolution.org/global/team/ocean-robbins-57.jpg"
          imgPosition="left"
          color="white"
          type="bottom"
          headlines={
            <>
              <h2 className="section-heading mb-2">Ocean Robbins</h2>
              <h4 className="m-0 text-400">A food revolution leader and inspiration to millions.</h4>
            </>
          }
          collapseHeader={
            <>
              <h5 className="text-400 m-0">Click Here to</h5>
              <h4 className="m-0">Learn More About Ocean Robbins</h4>
            </>
          }
          collapseHeaderBgColor="black"
          content={
            <>
              <p>
                Ocean Robbins was born in a log cabin built by his parents and was raised on food they grew on the land
                together. He has reached millions of people from 180+ nations in schools, conferences, and online
                events.
              </p>
              <p>
                Ocean’s work has taken him all over the world, where he has seen first-hand the powerful impact of the
                food we eat — not just on our health, but on people and economies everywhere. He has served as an
                adjunct professor for Chapman University and is author of the national bestseller,{' '}
                <i>31-Day Food Revolution.</i>
              </p>
              <p>He serves as CEO of Food Revolution Network.</p>
            </>
          }
        />

        <SectionTriangle id="" color="light-gray" type="none" innerClass="mobile-padding">
          <Container>
            <Row>
              <Col>
                <div className="background-white box-shadow-black-50 rounded text-center p-4 p-lg-5">
                  {fromQuiz && (
                    <>
                      <h2 className="section-heading mb-3">
                        Don’t just test your knowledge. Gain cutting edge wisdom that can transform your life.
                      </h2>
                      <h4 className="text-400 mt-0 mb-5">The power of the brain is at your fingertips.</h4>
                    </>
                  )}
                  <h2 className="section-heading text-purple mb-2">
                    <i>Don’t Miss Your Chance!</i>
                  </h2>
                  <h3 className="m-0">For a limited time watch the</h3>
                  <Logo className="my-4" />
                  <h3 className="text-purple mt-0">
                    <i>It’s completely FREE.</i>
                  </h3>

                  {defaultSchedule.phase === 'during' || (partnerName && specialSchedule.phase === 'during') ? (
                    <TimerSection
                      launchEnd={launchEnd}
                      timerHeading={
                        partnerName && specialSchedule.launchTitle
                          ? specialSchedule.launchTitle
                          : defaultSchedule.launchTitle
                      }
                    />
                  ) : null}

                  {defaultSchedule.phase === 'after' || (partnerName && specialSchedule.phase === 'after') ? (
                    <h4 className="mt-4 mb-2">
                      The{' '}
                      <span className="text-lowercase">
                        {partnerName && specialSchedule.launchTitle
                          ? specialSchedule.launchTitle
                          : defaultSchedule.launchTitle}
                      </span>{' '}
                      period is over... But we can still get you in!
                    </h4>
                  ) : null}

                  <div className="mt-4">{ctaButton}</div>
                </div>
              </Col>
            </Row>
          </Container>
        </SectionTriangle>
      </div>

      <div className="footer-content mt-auto">
        <Footer />
        {!noShare && <ShareButtons floating />}
      </div>

      <OptinModal />
      {!noExitIntent && <ModalExitLanding />}
    </div>
  );
}
